import React from 'react';
import { Typography } from '@axtria/design-system-react';
import NotificationRow from './NotificationRow';
import { NotificationRecord } from './NotificationTypes';

const SCROLL_LOAD_THRESHOLD = 48;

interface NotificationPanelProps {
  notifications: NotificationRecord[];
  unreadCount: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  onSelect: (_item: NotificationRecord) => void;
  onLoadMore: () => void;
}

const getStatusMessage = (isLoading: boolean, count: number) => {
  if (isLoading) return 'Loading notifications…';
  if (count === 0) return 'You’re all caught up.';
  return '';
};

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  notifications,
  unreadCount,
  isLoading,
  isLoadingMore,
  onSelect,
  onLoadMore,
}) => {
  const statusMessage = getStatusMessage(isLoading, notifications.length);

  // Infinite scroll: ask for the next slice once the list is near its end.
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight <= SCROLL_LOAD_THRESHOLD) {
      onLoadMore();
    }
  };

  return (
    <div className='notification-panel' role='dialog' aria-label='Notifications'>
      <div className='notification-panel-header'>
        <Typography level="paragraph" levelVariant="overlineAllcaps" textColor="grey.50">
          Notifications
        </Typography>
        {unreadCount > 0 && (
          <Typography level="ui" levelVariant="small400" textColor="grey.50" >
            {unreadCount} new
          </Typography>
        )}
      </div>
      <div className='notification-list' onScroll={handleScroll}>
        {statusMessage && (
          <div className='notification-empty'>
            <Typography level="ui" levelVariant="small500" textColor="grey.40">
              {statusMessage}
            </Typography>
          </div>
        )}
        {notifications.map((item) => (
          <NotificationRow key={item.id} item={item} onSelect={onSelect} />
        ))}
        {isLoadingMore && (
          <div className='notification-empty'>
            <Typography level="ui" levelVariant="small500" textColor="grey.40">
              Loading more…
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationPanel;
